// Subscribe gate — shown to a signed-in user whose subscription is anything
// other than 'active' / 'trialing'. Displays the three plan tiers and POSTs
// `{ userId, email, plan }` to /api/create-checkout-session, which picks the
// matching Stripe price (STRIPE_PRICE_ID_BASIC / _PREMIUM / _ENTERPRISE).
//
// Today every plan still drives a single-seat workspace because the data
// model doesn't have organizations yet. Premium/Enterprise show a "team
// support coming soon" note so customers aren't misled — multi-tenancy is
// the next sprint.

import React, { useEffect, useState } from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { useAuth } from '../auth/AuthContext';
import { useSubscription } from './SubscriptionContext';

// Support address for billing escalations. Configurable so we aren't
// shipping the operator's personal inbox to every paying customer.
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'support@sixfigureagentcrm.com';

// ONE simple price — $40/month per subscriber (2026-08). Every subscriber gets the full platform; add or
// remove people and the bill moves by $40. Keeps id:'basic' so checkout resolves the same Stripe
// lookup_key (set that price to $40 in Stripe).
const PLANS = [
  {
    id: 'basic',
    name: 'Six Figure Agent OS',
    price: 40,
    tagline: 'The AI Operating System for Insurance Agencies — give every team member their own AI-powered operating system.',
    seatNote: 'One platform. One price. $40/month per subscriber.',
    badge: 'Simple pricing',
    bullets: [
      'AI Assistant',
      'CRM',
      'Dialer',
      'SMS & Email',
      'Calendar',
      'Pipeline Management',
      'Automations',
      'AI Call Notes',
      'Reporting',
      'Compliance Tools',
      'Health · Medicare · Life Insurance Workspaces',
    ],
    cta: 'Start now — $40/subscriber',
    accent: 'emerald',
    teamSoon: false,
  },
];

export default function SubscribePage() {
  const { user, signOut } = useAuth();
  const { refreshSubscription, subscriptionStatus } = useSubscription();
  const [pendingPlan, setPendingPlan] = useState('');
  // Per-seat selection: Team bills per agent (min 1); Agency includes 8 seats
  // and bills $90/agent beyond that (min 8). Producer is always single-seat.
  const [seats, setSeats] = useState({ premium: 1, enterprise: 8 });
  const [error, setError] = useState('');
  const [returnedFromCheckout, setReturnedFromCheckout] = useState(null);
  // Post-checkout polling state. The webhook usually writes the
  // subscription row in 1-2s but Stripe occasionally takes ~30s. We
  // poll for up to ~30s after the success redirect so the user
  // doesn't have to refresh manually. pollCount tracks attempts so
  // we can show "checking… (3/10)" if it's slow, and pollDone gates
  // the manual-refresh fallback button.
  const [pollCount, setPollCount] = useState(0);
  const [pollDone, setPollDone] = useState(false);
  const [manualRefreshing, setManualRefreshing] = useState(false);

  // Pick up ?subscribe=success / ?subscribe=cancel on mount, show the
  // appropriate banner, clean the query string so a refresh doesn't repeat
  // the message, and sync-verify the Stripe session so activation
  // happens even if the webhook never lands. The polling loop below is
  // the safety net for the rare case where verify itself fails.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const status = params.get('subscribe');
    if (status !== 'success' && status !== 'cancel') return;

    // Capture BEFORE we strip the query. success flow needs session_id
    // to call /api/checkout-verify; delete after.
    const sessionId = params.get('session_id') || '';

    setReturnedFromCheckout(status);
    params.delete('subscribe');
    params.delete('session_id');
    params.delete('plan');
    const query = params.toString();
    const newUrl = window.location.pathname + (query ? `?${query}` : '');
    window.history.replaceState({}, '', newUrl);

    if (status !== 'success') return;

    // Fire-and-track sync verify. Not awaited — the polling loop
    // (below) will still run as a safety net if verify soft-fails
    // (e.g. 409 session_not_paid_yet on a race with Stripe's own
    // finalization). We refreshSubscription() both before AND after
    // verify so the UI always reflects the freshest state.
    refreshSubscription();
    if (sessionId) {
      (async () => {
        try {
          const { supabase } = await import('../lib/supabase');
          const token = readPersistedJwtDirect();
          const headers = { 'content-type': 'application/json' };
          if (token) headers.Authorization = `Bearer ${token}`;
          const r = await fetch('/api/checkout-verify', {
            method: 'POST',
            headers,
            body: JSON.stringify({ session_id: sessionId }),
          });
          // Whether it succeeds or 409s (not-paid-yet race), retry
          // subscription refresh — a success provisioned the rows,
          // a 409 means the webhook will eventually catch up and
          // the poll loop below picks it up.
          if (r.ok) await refreshSubscription();
        } catch {
          // Silent — poll loop remains the fallback.
        }
      })();
    }

    // FirstPromoter conversion event — fires client-side so the
    // affiliate's dashboard shows the conversion within ~30 sec,
    // independent of FirstPromoter's Stripe webhook (which may
    // arrive minutes later). trackConversion is a no-op when FP
    // isn't loaded so this is safe in any environment.
    try {
      const { trackConversion } = require('../lib/firstpromoter');
      trackConversion({ email: user?.email, uid: user?.id });
    } catch { /* never block UX */ }
  }, [refreshSubscription, user?.email, user?.id]);

  // Marketing-site deep link: /?subscribe=1&plan=<id> auto-scrolls the
  // matching plan card into view and highlights it. We do NOT auto-fire
  // checkout — the user should confirm agent count for team/agency
  // plans, and an unattended POST would blow past the seat picker.
  // Accepts the marketing-handoff names (producer/team/agency) as
  // aliases for the internal IDs (basic/premium/enterprise) so a stale
  // URL with plan=producer still lands somewhere sane.
  //
  // Guards: unknown plan key → silently ignored (page still renders).
  //         Never crashes — the "team has been notified" screen the
  //         operator was seeing was a RootErrorBoundary hit, not this
  //         handler.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('subscribe');
    if (flag !== '1' && flag !== 'true' && flag !== 'yes') return;
    const rawPlan = String(params.get('plan') || '').trim().toLowerCase();
    if (!rawPlan) return;
    const PLAN_ALIASES = {
      basic: 'basic', producer: 'basic', producer_monthly: 'basic', solo: 'basic',
      premium: 'premium', team: 'premium', team_seat: 'premium', team_seat_monthly: 'premium',
      enterprise: 'enterprise', agency: 'enterprise', agency_base: 'enterprise', agency_base_monthly: 'enterprise',
    };
    const resolved = PLAN_ALIASES[rawPlan];
    if (!resolved) return;
    // Defer to next tick so the plan cards have mounted, then scroll
    // + highlight the matching card. Keeps this cosmetic — no
    // side-effect chain that could crash.
    const t = setTimeout(() => {
      try {
        const el = document.getElementById(`plan-card-${resolved}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.style.boxShadow = '0 0 0 3px rgba(16,185,129,0.55)';
          setTimeout(() => { try { el.style.boxShadow = ''; } catch {} }, 2400);
        }
      } catch { /* cosmetic — never crash the page */ }
      try {
        const next = new URLSearchParams(window.location.search);
        next.delete('subscribe');
        next.delete('plan');
        const q = next.toString();
        window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
      } catch { /* URL cleanup is best-effort */ }
    }, 250);
    return () => clearTimeout(t);
  }, []);

  // Capture ?ref= affiliate code on landing and persist to localStorage (90-day window)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (!ref) return;
    const cleaned = ref.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 64);
    if (!cleaned) return;
    try {
      localStorage.setItem('sfa_ref', cleaned);
      localStorage.setItem('sfa_ref_at', String(Date.now()));
    } catch { /* private mode */ }
  }, []);

  // Capture ?trial= / ?code= free-trial code on landing and persist
  // (survives the auth round-trip), mirroring the ?ref= capture above.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const p = new URLSearchParams(window.location.search);
    const t = p.get('trial') || p.get('code');
    if (!t) return;
    const cleaned = t.trim().toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 32);
    if (!cleaned) return;
    try { localStorage.setItem('sfa_trial', cleaned); } catch { /* private mode */ }
  }, []);

  // Capture ?lp= foot-in-the-door landing-page slug (Funnel-A attribution),
  // so a trial started from a SCOUT page is credited back to that page.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const lp = new URLSearchParams(window.location.search).get('lp');
    if (!lp) return;
    const cleaned = lp.replace(/[^a-zA-Z0-9]/g, '').slice(0, 32);
    if (cleaned) try { localStorage.setItem('sfa_lp', cleaned); } catch { /* private mode */ }
  }, []);

  // Polling loop. Runs only after a success redirect, only while status
  // is still not active/trialing, and only up to MAX_POLLS attempts.
  // Each tick calls refreshSubscription; SubscriptionContext updates
  // the status which feeds back into this effect's dep array.
  useEffect(() => {
    if (returnedFromCheckout !== 'success') return;
    if (subscriptionStatus === 'active' || subscriptionStatus === 'trialing') return;
    const MAX_POLLS = 10;
    const INTERVAL_MS = 3000;
    if (pollCount >= MAX_POLLS) {
      setPollDone(true);
      return;
    }
    const t = setTimeout(() => {
      setPollCount(c => c + 1);
      refreshSubscription();
    }, pollCount === 0 ? 1000 : INTERVAL_MS);
    return () => clearTimeout(t);
  }, [returnedFromCheckout, subscriptionStatus, pollCount, refreshSubscription]);

  // Manual fallback. If the polling window expired (or the user came
  // back later from email + clicked "I paid"), this button lets them
  // re-query without a full page reload.
  const manualRefresh = async () => {
    if (manualRefreshing) return;
    setManualRefreshing(true);
    await refreshSubscription();
    setManualRefreshing(false);
    setPollDone(false);
    setPollCount(0);
  };

  const readStoredReferral = () => {
  // FirstPromoter (Flow 2) is the canonical affiliate channel —
  // prefer its cookie/localStorage when present so the affiliate
  // gets credit through their dashboard.
  try {
    const { readAffiliateCode } = require('../lib/firstpromoter');
    const fpr = readAffiliateCode();
    if (fpr) return fpr;
  } catch { /* module load failed — fall through to legacy */ }
  // Legacy sfa_ref capture (pre-FirstPromoter Partner OS) — kept so
  // any in-flight referral links from the old flow still attribute.
  try {
    const code = localStorage.getItem('sfa_ref') || '';
    const at   = Number(localStorage.getItem('sfa_ref_at') || 0);
    const NINETY_DAYS = 90 * 24 * 3600 * 1000;
    if (!code || !at || Date.now() - at > NINETY_DAYS) return '';
    return code;
  } catch { return ''; }
};

const readStoredTrial = () => {
  try { return localStorage.getItem('sfa_trial') || ''; } catch { return ''; }
};

const readStoredLp = () => {
  try { return localStorage.getItem('sfa_lp') || ''; } catch { return ''; }
};

const startCheckout = async (planId, seatCount) => {
    if (pendingPlan) return;
    if (!user?.id || !user?.email) {
      setError('Missing account details — please sign out and back in.');
      return;
    }
    setError('');
    setPendingPlan(planId);
    try {
      // Canonical entry point — /api/checkout uses Stripe lookup_keys
      // (portable across test/live mode), $49 activation fee when
      // configured, and the §11a custom_fields (EIN/business). The
      // legacy /api/create-checkout-session is still wired for any
      // older callers but new ones should use this route.
      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ userId: user.id, email: user.email, plan: planId, seats: Math.max(1, seatCount || 1), referral_code: readStoredReferral(), trial_code: readStoredTrial(), landing_page_slug: readStoredLp() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        throw new Error(data?.error || `Checkout request failed (${res.status}).`);
      }
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || 'Could not start Checkout. Please try again.');
      setPendingPlan('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 py-8 px-4">
      <button
        onClick={signOut}
        className="fixed top-4 right-4 text-xs text-slate-300 hover:text-white hover:underline z-10"
      >
        Sign out
      </button>

      <div className="max-w-none mx-auto">
        <div className="text-center mb-8 text-white">
          <div className="w-12 h-12 mx-auto rounded-lg bg-emerald-600 flex items-center justify-center text-white font-bold text-2xl">$</div>
          <h1 className="text-3xl font-bold mt-3">Simple Pricing</h1>
          <p className="text-sm text-slate-300 mt-2 max-w-xl mx-auto"><strong>$40/month per subscriber.</strong> The AI Operating System for Insurance Agencies — no contracts, cancel anytime, scale up or down as your team grows.</p>
          {user?.email && <p className="text-xs text-slate-400 mt-2">Signed in as <span className="font-mono">{user.email}</span></p>}
        </div>

        {returnedFromCheckout === 'success' && (
          <div className="max-w-none mx-auto bg-emerald-50 border-2 border-emerald-300 text-emerald-900 rounded-lg px-4 py-3 mb-6">
            <div className="flex items-start gap-3">
              <div className="text-2xl">{pollDone ? '⚠' : '⏳'}</div>
              <div className="flex-1">
                <div className="font-bold">
                  {pollDone
                    ? 'Payment received — still waiting on the activation signal'
                    : 'Payment received! Activating your account…'}
                </div>
                <div className="text-sm mt-1">
                  {pollDone ? (
                    <>
                      Stripe took longer than expected to send us the activation event. Click <strong>Refresh status</strong> below.
                      If that doesn't work, your payment is safe — please email <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">{SUPPORT_EMAIL}</a> with the details under "your account info" below and we'll activate you manually within a few hours.
                    </>
                  ) : (
                    <>Checking… ({Math.min(pollCount + 1, 10)}/10) — this usually finishes in a few seconds.</>
                  )}
                </div>
                {pollDone && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={manualRefresh}
                      disabled={manualRefreshing}
                      className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-1.5 rounded-lg"
                    >
                      {manualRefreshing ? 'Checking…' : 'Refresh status'}
                    </button>
                    <span className="text-xs text-emerald-800">Current status: <span className="font-mono font-bold">{subscriptionStatus}</span></span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {returnedFromCheckout === 'cancel' && (
          <div className="max-w-none mx-auto bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-3 text-sm mb-6">
            Subscription canceled. You can pick a plan again anytime.
          </div>
        )}
        {error && (
          <div className="max-w-none mx-auto bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-6">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-5 max-w-md mx-auto">
          {PLANS.map(plan => {
            const isPending = pendingPlan === plan.id;
            const otherPending = !!pendingPlan && !isPending;
            const planSeats = plan.id in seats ? seats[plan.id] : 1;
            const computedTotal = plan.perAgent
              ? plan.price * planSeats
              : plan.includedSeats
                ? plan.price + plan.extraSeatPrice * Math.max(0, planSeats - plan.includedSeats)
                : plan.price;
            const accentBorder = plan.accent === 'emerald' ? 'border-emerald-300' : plan.accent === 'violet' ? 'border-violet-300 ring-2 ring-violet-200' : 'border-amber-300';
            const accentText = plan.accent === 'emerald' ? 'text-emerald-700' : plan.accent === 'violet' ? 'text-violet-700' : 'text-amber-700';
            const accentBtn = plan.accent === 'emerald' ? 'bg-emerald-600 hover:bg-emerald-700' : plan.accent === 'violet' ? 'bg-violet-600 hover:bg-violet-700' : 'bg-amber-600 hover:bg-amber-700';
            const accentBadge = plan.accent === 'emerald' ? 'bg-emerald-100 text-emerald-700' : plan.accent === 'violet' ? 'bg-violet-100 text-violet-700' : 'bg-amber-100 text-amber-700';
            return (
              <div key={plan.id} id={`plan-card-${plan.id}`} className={`bg-white rounded-2xl shadow-xl border-2 ${accentBorder} p-6 flex flex-col transition-shadow duration-300`}>
                {plan.badge && (
                  <div className="text-center -mt-9 mb-2">
                    <span className={`inline-block ${accentBadge} text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow`}>{plan.badge}</span>
                  </div>
                )}
                <div className="text-center mb-4">
                  <h2 className={`text-lg font-bold ${accentText}`}>{plan.name}</h2>
                  <div className="mt-2">
                    <span className="text-4xl font-bold text-slate-900">${plan.price}</span>
                    <span className="text-slate-500 text-sm">{plan.perAgent ? '/agent/mo' : '/mo per subscriber'}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed">{plan.tagline}</p>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold text-slate-700 text-center mb-4">
                  {plan.seatNote}
                </div>

                {(plan.perAgent || plan.includedSeats) && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-4">
                    <div className="flex items-center justify-between gap-2">
                      <label className="text-[11px] font-semibold text-slate-700">{plan.perAgent ? 'How many agents?' : 'Total agents (8 included)'}</label>
                      <input
                        type="number"
                        min={plan.minSeats || 1}
                        value={planSeats}
                        onChange={(e) => {
                          const floor = plan.minSeats || 1;
                          const n = Math.max(floor, parseInt(e.target.value, 10) || floor);
                          setSeats(s => ({ ...s, [plan.id]: n }));
                        }}
                        className="w-16 border border-slate-300 rounded px-2 py-1 text-sm text-right"
                      />
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      {plan.perAgent
                        ? `${planSeats} × $${plan.price} = $${computedTotal}/mo`
                        : planSeats > plan.includedSeats
                          ? `$${plan.price} + ${planSeats - plan.includedSeats} extra × $${plan.extraSeatPrice} = $${computedTotal}/mo`
                          : `$${plan.price}/mo (up to ${plan.includedSeats} agents)`}
                    </div>
                  </div>
                )}

                <ul className="space-y-2 text-sm text-slate-700 mb-5 flex-1">
                  {plan.bullets.map(b => (
                    <li key={b} className="flex items-start gap-2">
                      <span className={`${accentText} mt-0.5`}>✓</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => startCheckout(plan.id, planSeats)}
                  disabled={isPending || otherPending}
                  className={`w-full ${accentBtn} disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-lg`}
                >
                  {isPending ? 'Redirecting to Stripe…' : plan.cta}
                </button>
                <p className="text-[10px] text-slate-400 text-center mt-2">One-time activation per subscriber (texting number + A2P) · No contracts — cancel anytime</p>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Questions? Email <a href={`mailto:${SUPPORT_EMAIL}`} className="underline hover:text-slate-200">{SUPPORT_EMAIL}</a>
        </p>

        {/* Your account info — copy-paste block for support emails when
            the polling / webhook handshake fails. Surfaced only after a
            success redirect so existing visitors aren't distracted. */}
        {returnedFromCheckout === 'success' && pollDone && user && (
          <div className="max-w-md mx-auto mt-4 bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-xs text-slate-300">
            <div className="font-bold text-slate-200 mb-1">Your account info (include in support email)</div>
            <div className="font-mono break-all">
              <div>Email: {user.email}</div>
              <div>User ID: {user.id}</div>
              <div>Status: {subscriptionStatus}</div>
              <div>Time: {new Date().toISOString()}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
